import React, { useEffect, useState } from "react";
import Loading from "components/Shared/Loading/Loading";
import { audio } from "services/audioManager";
import { IMAGES } from "constants/constants";

function flatten(obj) {
    if (!obj) return [];
    return Object.values(obj).flatMap(v => (typeof v === "string" ? [v] : flatten(v)));
}

export default function Preloader({ imagesToLoad, fontsToLoad, audioToLoad, onComplete }) {
    const [done, setDone] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        let cancelled = false;

        // 1️⃣ Priority images
        const priorityImages = [
            IMAGES.SHARED.EmptyBG,
            IMAGES.SHARED.GCLogoWhite,
        ].filter(Boolean);

        // 2️⃣ Remaining images (excluding priority)
        const imgList = Array.isArray(imagesToLoad)
            ? imagesToLoad.filter(src => !priorityImages.includes(src))
            : [];

        const fontList = Array.isArray(fontsToLoad) ? fontsToLoad : [];
        const audioList = Array.isArray(audioToLoad) ? audioToLoad : [];

        // 3️⃣ Total steps
        const total =
            priorityImages.length +
            imgList.length +
            fontList.length +
            audioList.length;

        if (total === 0) {
            onComplete(); 
            setDone(true);
            return;
        }

        const step = 100 / total;
        const bump = () => !cancelled && setProgress(p => Math.min(100, p + step));

        // ---- Loaders ----
        const loadImage = src =>
            new Promise(res => {
                const img = new Image();
                img.onload = img.onerror = () => { bump(); res(); };
                img.src = src;
            });

        const loadFont = font =>
            new Promise(res => {
                if (!window.FontFace) { bump(); return res(); }
                const fontFace = new FontFace(font.font, `url(${font.path})`);
                fontFace.load().then(
                    () => { document.fonts.add(fontFace); bump(); res(); },
                    () => { bump(); res(); }
                );
            });

        const loadAudio = def =>
            audio.primeClip(def)
                .then(() => bump())
                .catch(() => bump());

        // ---- Sequential pipeline ----
        const loadSequentially = async () => {
            try {
                // Priority images FIRST
                for (const src of priorityImages) {
                    if (cancelled) return;
                    await loadImage(src);
                }

                // Remaining images
                for (const src of imgList) {
                    if (cancelled) return;
                    await loadImage(src);
                }

                // Fonts
                for (const font of fontList) {
                    if (cancelled) return;
                    await loadFont(font);
                }

                // Audio
                for (const clip of audioList) {
                    if (cancelled) return;
                    await loadAudio(clip);
                }

                if (!cancelled) {
                    onComplete(); 
                    setDone(true);
                }
            } catch {
                if (!cancelled) {
                    onComplete();
                    setDone(true);
                }
            }
        };

        loadSequentially();

        return () => { cancelled = true; };
    }, [imagesToLoad, fontsToLoad, audioToLoad]);

    if (!done) return <Loading loadingText="" progress={progress} />;
    return null;
}

export { flatten };